// packages/web/src/hooks/useServicePricing.ts
// Hook for calculating service pricing based on selections

import { useMemo } from 'react';

import { MarketplaceService } from '../types/pricing';

interface UseServicePricingResult {
  selectedVariantPrice: number | undefined;
}

export const useServicePricing = (
  service: MarketplaceService | undefined,
  selectedVariant: string | undefined,
  selectedFrequency: string | undefined,
  selectedTerm?: number | null,
): UseServicePricingResult => {
  const selectedVariantPrice = useMemo(() => {
    if (!service) return undefined;

    if (service.pricing_model === 'variants' && service.variants && selectedVariant) {
      const variant = service.variants.variants.find((v) => v.key === selectedVariant);
      if (variant) {
        // Check if service has contract terms and use term-specific pricing
        let pricing = variant.pricing;
        const hasContractTerms =
          service.contract_term_months && service.contract_term_months.length > 0;

        if (hasContractTerms && selectedTerm && variant.term_overrides) {
          // Use term-specific pricing for contract services
          const termOverride = variant.term_overrides.find(
            (override: any) => override.term_months === selectedTerm,
          );
          if (termOverride) {
            pricing = termOverride;
          }
        }

        return pricing.min_recurring_cents || pricing.recurring_cents;
      }
    } else if (
      service.pricing_model === 'multi_frequency' &&
      service.frequencies &&
      selectedFrequency
    ) {
      const freq = service.frequencies.find((f) => f.frequency === selectedFrequency);
      if (freq) {
        return freq.recurring_cents;
      }
    } else if (service.pricing_model === 'varying' && service.default_pricing) {
      if (selectedVariant && service.variants) {
        const variant = service.variants.variants.find((v) => v.key === selectedVariant);
        if (variant) {
          let pricing = variant.pricing;
          const hasContractTerms =
            service.contract_term_months && service.contract_term_months.length > 0;
          if (hasContractTerms && selectedTerm && variant.term_overrides) {
            const termOverride = variant.term_overrides.find(
              (override: any) => override.term_months === selectedTerm,
            );
            if (termOverride) {
              pricing = termOverride;
            }
          }
          return pricing.min_recurring_cents || pricing.recurring_cents;
        }
      }
      // For varying prices without variant selection, use the minimum price for cart calculations
      return service.default_pricing.min_recurring_cents;
    } else if (service.default_pricing) {
      return service.default_pricing.recurring_cents;
    }

    return undefined;
  }, [service, selectedVariant, selectedFrequency, selectedTerm]);

  return {
    selectedVariantPrice,
  };
};
